import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Form from 'components/elements/Form/form';
import Input from 'components/elements/Form/Input/input';
import { useAuth } from 'services/auth-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';

// Interface for Login form
interface LoginInterface {
  username: string;
  password: string;
}

/* The code is defining a validation schema using the `yup` library. If these validation rules are not met, an error message will be displayed. */
const LoginSchema = yup.object().shape({
  username: yup.string().required('Username is required'),
  password: yup
    .string()
    .max(32, 'Maximum password length is 32 characters')
    .required('Password is required')
});

const LoginForm = () => {
  /* The below code is using the `useAuth` hook to access the `login` function from the authentication service. 
  This allows the component to call the `login` function to authenticate the user. */
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isloading, setIsloading] = useState(false);
  const [responseError, setResponseError] = useState('');

  /* The below line of code is using the `useForm` hook from the `react-hook-form` library to create a form instance. The `resolver` option is 
  used to specify the validation schema for the form, which in this case is `yupResolver(LoginSchema)`. This means that the form will be validated 
  using the `LoginSchema` defined using the `yup` library. */
  const methods = useForm({ resolver: yupResolver(LoginSchema) });

  const onSubmit = async (data: LoginInterface) => {
    try {
      setIsloading(true);

      /* The line `const response: any = await login(data.username, data.password);` is calling the
      `login` function from the authentication service and passing the `username` and `password` as
      parameters. The `await` keyword is used to wait for the login function to complete and return
      a response. */
      const response: any = await login(data.username, data.password);

      /* The below code block sets the `isLoading` useState variable to 'false' if the credentials are valid. */
      if (response?.role) {
        setIsloading(false);
      }

      /* The below code block returns an error message if the credentials are invalid. */
      if (!response?.sub) {
        setIsloading(false);
        setResponseError('Invalid username or password');
        return;
      }

      /**
       * This code block is determining the appropriate route to navigate to based on the user's role after successful login.
       * If the user's role is either 'SUPER' or 'ACCOUNT' the user will be redirected to the `/accounts` page.
       * If the user's role is 'BUILDING' the user will be redirected to the `/buildings` page.
       * If the user's role is 'ORG' the user will be redirected to the `/organizations` page.
       * If the user's role doesn't match with any of the ones specified then the user will be navigated to the `/` Login page.
       */
      if (response?.role === 'SUPER' || response?.role === 'ACCOUNT') {
        navigate('/accounts', { replace: true });
      } else if (response?.role === 'BUILDING') {
        navigate('/buildings', { replace: true });
      } else if (response?.role === 'ORG') {
        navigate('/organizations', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.log(error);
    }
  };

  /* The code is using a conditional rendering statement to display either a loading spinner or a login
  form based on the value of the `isloading` variable. */
  return isloading ? (
    <div className="flex justify-center items-center">
      <LoadingSpinner />
    </div>
  ) : (
    <Form buttonLabel="Sign In" methods={methods} onSubmit={onSubmit} styles="w-full px-2.5">
      <Input
        label="USERNAME"
        name="username"
        type="text"
        data-testid="example"
        placeholder="sam.smith@b-line.io"
        autoFocus
      />
      <Input
        label="PASSWORD"
        name="password"
        type="password"
        data-testid="example"
        placeholder="*****"
      />
      <div className="flex justify-start">
        <Link
          to="forgot-password"
          className="underline font-sm text-left block"
          style={{ color: '#34A5AC' }}
        >
          Forgot Password?
        </Link>
      </div>
      {responseError ? <div className="text-secondary2">{responseError}</div> : null}
    </Form>
  );
};

export default LoginForm;
